import { type ReactNode } from 'react';
import { View } from 'react-native';

import { useAuth } from '@/lib/auth';
import { useLocalCache } from '@/lib/powersync/provider';

import { StaleCacheScreen } from './StaleCacheScreen';

/**
 * Withholds the app's data screens until the local database is proven to belong to
 * the signed-in user.
 *
 * Every screen below reads local SQLite directly through `useQuery`, so a cache
 * that may still hold the previous account's rows (a sign-out whose clear failed)
 * must never reach them — connecting or not to the sync service is beside the
 * point. This sits inside the `(app)` group rather than around the router: gating
 * above the navigator would unmount it mid-navigation and lose the route.
 */
export function LocalCacheGate({ children }: { children: ReactNode }) {
  const cache = useLocalCache();
  // AuthProvider's, not supabase's: a bare `supabase.auth.signOut()` is a dead button with no
  // signal, and this screen is usually reached without one. See `AuthProvider.signOut`.
  const { signOut } = useAuth();

  if (cache.phase === 'error') {
    return <StaleCacheScreen onRetry={cache.retry} onSignOut={() => void signOut()} />;
  }

  if (cache.phase !== 'ready') {
    // Preparing: one storage read plus at most one SQLite transaction, so this is
    // brief and works offline. Matches the blank canvas shown while auth initializes.
    return <View className="flex-1 items-center justify-center bg-background" />;
  }

  return <>{children}</>;
}
